/**
 * monitoring — thin provider abstraction over error-reporting SDKs.
 *
 * Why this exists
 * ---------------
 * Coupling call sites directly to `@sentry/react` makes the SDK hard to swap
 * and impossible to silence in unit tests. This module exposes a stable
 * `monitoring` object whose implementation is injected once at startup via
 * `initMonitoring()`. Swapping to Crashlytics, Datadog, or a no-op is a
 * one-line change in `main.tsx`.
 *
 * Usage
 * -----
 *   // main.tsx
 *   import { initMonitoring } from '@/lib/monitoring';
 *   import { sentryProvider } from '@/lib/sentryProvider';
 *   if (import.meta.env.VITE_SENTRY_DSN) initMonitoring(sentryProvider);
 *
 *   // anywhere else
 *   import { monitoring } from '@/lib/monitoring';
 *   monitoring.captureException(error, { stage: 'storage_upload' });
 */

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export interface MonitoringProvider {
  /**
   * Report a caught exception with optional structured context.
   * Context keys are arbitrary — the provider surfaces them as "extra" data.
   */
  captureException(error: unknown, context?: Record<string, unknown>): void;

  /**
   * Report a plain message (not tied to a thrown error).
   * Useful for "this code path should never happen" breadcrumbs.
   */
  captureMessage?(message: string, level?: 'info' | 'warning' | 'error'): void;

  /**
   * Attach the authenticated user to subsequent events so reports are
   * traceable. Pass `null` on logout to clear the identity.
   */
  setUser?(user: { id: string; email?: string } | null): void;
}

// ---------------------------------------------------------------------------
// Null provider — used before initMonitoring() is called (or in tests).
// ---------------------------------------------------------------------------

const nullProvider: MonitoringProvider = {
  captureException: () => {},
  captureMessage:   () => {},
  setUser:          () => {},
};

// ---------------------------------------------------------------------------
// Singleton façade — call sites depend only on this, never on the provider.
// ---------------------------------------------------------------------------

let _provider: MonitoringProvider = nullProvider;

export const monitoring = {
  captureException(error: unknown, context?: Record<string, unknown>): void {
    _provider.captureException(error, context);
  },

  captureMessage(
    message: string,
    level: 'info' | 'warning' | 'error' = 'info',
  ): void {
    _provider.captureMessage?.(message, level);
  },

  setUser(user: { id: string; email?: string } | null): void {
    _provider.setUser?.(user);
  },
} as const;

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

/**
 * Register the active provider. Call once at application startup, before any
 * `monitoring.*` calls. Safe to call multiple times (e.g. in tests) — each
 * call replaces the previous provider.
 */
export function initMonitoring(provider: MonitoringProvider): void {
  _provider = provider;
}

/** Reset to the no-op provider — intended for use in test teardowns. */
export function resetMonitoringForTests(): void {
  _provider = nullProvider;
}
